/**
 * JsonEditorHelper - Simple API for the JSON Forms Editor
 *
 * This helper eliminates the need to write verbose schema/uiSchema payloads.
 * Just define your parameters in a simple array and it handles the rest.
 *
 * USAGE EXAMPLES:
 *
 *   // Example 1: Simple parameter list
 *   JsonEditorHelper.openSimple("Edit MOSFET", [
 *     { name: "VTO", type: "number", title: "VTO (V)", default: 0.7, min: -5, max: 5 },
 *     { name: "BETA", type: "number", title: "BETA", default: 0.0001, min: 0 }
 *   ], { VTO: 0.7, BETA: 0.0001 }, (result) => {
 *     console.log("VTO=" + result.VTO + " BETA=" + result.BETA);
 *   });
 *
 *   // Example 2: Grouped parameters
 *   JsonEditorHelper.open({
 *     title: "MOSFET Model",
 *     groups: [
 *       {
 *         label: "Basic",
 *         params: [
 *           { name: "VTO", type: "number", title: "Threshold", default: 0.7 },
 *           { name: "BETA", type: "number", title: "BETA", default: 0.0001 }
 *         ]
 *       },
 *       {
 *         label: "Advanced",
 *         params: [
 *           { name: "LAMBDA", type: "number", title: "Lambda", default: 0.02 }
 *         ]
 *       }
 *     ],
 *     data: { VTO: 0.7, BETA: 0.0001, LAMBDA: 0.02 },
 *     onSave: (result) => console.log(result)
 *   });
 */

const JsonEditorHelper = (function() {
  'use strict';

  /**
   * Main entry point - open editor with grouped parameters
   */
  function open(config) {
    if (typeof window.openJsonEditor !== 'function') {
      console.error('[JsonEditorHelper] window.openJsonEditor not available. Make sure LabLayout is mounted.');
      return;
    }

    window.openJsonEditor(buildConfig(config));
  }

  /**
   * Simplified entry point - flat list of parameters (auto-grouped)
   */
  function openSimple(title, params, data, onSave) {
    // Helper function to get localized string
    const lt = (key, defaultValue) => {
      try {
        const keys = key.split('.');
        let value = typeof window !== 'undefined' && window.l;
        for (const k of keys) {
          if (value && typeof value === 'object') {
            value = value[k];
          } else {
            value = null;
            break;
          }
        }
        return value || defaultValue;
      } catch (e) {
        return defaultValue;
      }
    };

    open({
      title: title,
      groups: [{ label: lt('spice_input.properties', 'Properties'), params: params }],
      data: data,
      onSave: onSave
    });
  }

  /**
   * Even simpler - just key-value pairs with inferred types
   */
  function openFromData(title, data, onSave) {
    const params = Object.keys(data).map(key => {
      const value = data[key];
      const param = { name: key, default: value };

      if (typeof value === 'number') {
        param.type = value % 1 === 0 ? 'integer' : 'number';
        param.title = key;
      } else if (typeof value === 'boolean') {
        param.type = 'boolean';
        param.title = key;
      } else {
        param.type = 'string';
        param.title = key;
      }

      return param;
    });

    openSimple(title, params, data, onSave);
  }

  /**
   * Build full JSONForms config from simple definition
   */
  function buildConfig(config) {
    const schema = { type: 'object', properties: {}, required: [] };
    const uiSchemaElements = [];

    for (const group of config.groups) {
      const groupElements = [];

      for (const param of group.params) {
        const propSchema = { type: param.type };

        // Title defaults to param name if not provided
        propSchema.title = param.title || param.name;

        if (param.description) propSchema.description = param.description;
        if (param.default !== undefined) propSchema.default = param.default;

        // Number/integer specific
        if (param.type === 'number' || param.type === 'integer') {
          if (param.min !== undefined) propSchema.minimum = param.min;
          if (param.max !== undefined) propSchema.maximum = param.max;
          // Only set multipleOf if step is a number (not 'any')
          if (param.step !== undefined && typeof param.step === 'number') {
            propSchema.multipleOf = param.step;
          }
        }

        // Enum handling
        if (param.enum) {
          propSchema.enum = param.enum;
          if (param.enumNames) propSchema.enumNames = param.enumNames;
        }

        // String pattern
        if (param.pattern) propSchema.pattern = param.pattern;

        schema.properties[param.name] = propSchema;

        // Add to required if specified
        if (param.required || config.groups.indexOf(group) === 0) {
          schema.required.push(param.name);
        }

        // UI control with optional className support
        const uiControl = {
          type: 'Control',
          scope: `#/properties/${param.name}`
        };

        // Add options for custom styling (e.g., className for select dropdowns)
        if (param.options) {
          uiControl.options = param.options;
        }

        groupElements.push(uiControl);
      }

      uiSchemaElements.push({
        type: 'Group',
        label: group.label,
        elements: groupElements
      });
    }

    return {
      schema: schema,
      uiSchema: {
        type: 'VerticalLayout',
        elements: uiSchemaElements
      },
      data: config.data,
      title: config.title || 'Edit Properties',
      onSave: config.onSave
    };
  }

  /**
   * Pre-defined parameter templates for common components
   */
  const Templates = {
    // MOSFET parameters
    mosfet: {
      params: [
        { name: 'VTO', type: 'number', title: 'Threshold (V)', default: 0.7, min: -5, max: 5 },
        { name: 'BETA', type: 'number', title: 'BETA', default: 0.0001, min: 0 },
        { name: 'LAMBDA', type: 'number', title: 'Lambda (1/V)', default: 0.02, min: 0, max: 1 },
        { name: 'LEVEL', type: 'integer', title: 'SPICE Level', enum: [1, 2, 3], enumNames: ['Level 1', 'Level 2', 'Level 3'], default: 1 }
      ]
    },

    // Resistor parameters
    resistor: {
      params: [
        { name: 'resistance', type: 'number', title: 'Resistance (Ω)', default: 1000, min: 0.001 },
        { name: 'tolerance', type: 'number', title: 'Tolerance (%)', default: 5, min: 0, max: 50 },
        { name: 'power', type: 'number', title: 'Power (W)', default: 0.25, min: 0 }
      ]
    },

    // Capacitor parameters
    capacitor: {
      params: [
        { name: 'capacitance', type: 'number', title: 'Capacitance (F)', default: 1e-6, min: 1e-12 },
        { name: 'voltageRating', type: 'number', title: 'Voltage Rating (V)', default: 50, min: 0 }
      ]
    }
  };

  /**
   * Open editor using a template
   */
  function openFromTemplate(templateName, data, title, onSave) {
    const template = Templates[templateName];
    if (!template) {
      console.error('[JsonEditorHelper] Unknown template:', templateName);
      return;
    }
    openSimple(title || 'Edit Properties', template.params, data, onSave);
  }

  // Export API
  return {
    open: open,
    openSimple: openSimple,
    openFromData: openFromData,
    openFromTemplate: openFromTemplate,
    Templates: Templates
  };
})();

// Auto-expose to window
if (typeof window !== 'undefined') {
  window.JsonEditorHelper = JsonEditorHelper;
  console.log('[JsonEditorHelper] Loaded. Usage:');
  console.log('  JsonEditorHelper.openSimple("Title", params, data, onSave)');
  console.log('  JsonEditorHelper.openFromTemplate("mosfet", data, "Edit MOSFET", onSave)');
  console.log('  JsonEditorHelper.openFromData("Title", data, onSave) - auto-detect types');
}

export default JsonEditorHelper;
